import React, { useEffect, useState } from "react";
import { apiUrl } from "../../lib/apiBase";

/**
 * Licence banner shown only when this site is served by an Atomizer
 * Embedded device (/api/embedded/device/status → embedded: true). On
 * electronvisual.org the endpoint answers {embedded: false} and nothing
 * renders.
 *
 *   trial    "Free trial: N days left" + Enter licence key
 *   grace    "Offline for a while: reconnect within N days"
 *   expired  "New calculations are paused; cached molecules still work"
 *   licensed nothing (or a renew notice in the last 14 days)
 */
type DeviceStatus = {
  embedded: boolean;
  mode?: "trial" | "licensed" | "grace" | "expired";
  reason?: string | null;
  days_left?: number;
  plan?: string;
  buy_url?: string;
  manage_url?: string;
};

export default function EmbeddedLicenseBanner() {
  const [st, setSt] = useState<DeviceStatus | null>(null);
  const [open, setOpen] = useState(false);
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetch(apiUrl("/api/embedded/device/status"))
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setSt(d))
      .catch(() => {});
  }, []);

  if (!st?.embedded || !st.mode) return null;
  if (st.mode === "licensed" && st.reason !== "renew_soon") return null;

  const days = st.days_left ?? 0;
  const dayWord = `${days} day${days === 1 ? "" : "s"}`;
  let message: string;
  if (st.mode === "trial") message = `Atomizer Embedded free trial: ${dayWord} left.`;
  else if (st.mode === "grace")
    message = `This device hasn't reached electronvisual.org recently. Connect it to the internet within ${dayWord} to keep calculations running.`;
  else if (st.mode === "licensed") message = `Your Atomizer Embedded licence ends in ${dayWord}.`;
  else
    message =
      (st.reason === "trial_ended"
        ? "The free trial on this device has ended."
        : st.reason === "offline_too_long"
          ? "This device has been offline for more than 30 days."
          : "The licence on this device has expired.") +
      " Cached molecules are still available; new calculations are paused.";

  const activate = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await fetch(apiUrl("/api/embedded/device/activate"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ key: key.trim() }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
      setSt(d);
      setOpen(false);
    } catch (err: any) {
      setError(err?.message || "Activation failed.");
    } finally {
      setBusy(false);
    }
  };

  const tone = st.mode === "expired" ? "var(--ev-red, #ef4444)" : "var(--ev-yellow, #eab308)";
  return (
    <div role="status" className="border-b-2 bg-ev-surface px-4 py-3 text-[15px] text-ev-fg" style={{ borderColor: tone }}>
      <div className="mx-auto flex max-w-ev flex-wrap items-center gap-x-4 gap-y-2">
        <span className="flex-1 min-w-[240px]">{message}</span>
        {st.mode !== "grace" && (
          <>
            <button type="button" className="ev-btn ev-btn-outline" onClick={() => setOpen((o) => !o)}>
              Enter licence key
            </button>
            <a className="ev-link" href={st.buy_url || "https://electronvisual.org/#embedded"} target="_blank" rel="noreferrer">
              Buy a licence
            </a>
          </>
        )}
      </div>
      {open && (
        <form onSubmit={activate} className="mx-auto mt-3 flex max-w-ev flex-wrap gap-2">
          <input
            className="min-h-[44px] flex-1 min-w-[240px] border-2 border-ev-fg bg-ev-bg px-3 text-[13px] text-ev-fg"
            placeholder="AEL1.…"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            required
          />
          <button type="submit" className="ev-btn ev-btn-primary disabled:opacity-60" disabled={busy}>
            {busy ? "Activating…" : "Activate"}
          </button>
          {error && <span className="w-full text-sm text-ev-red" role="alert">{error}</span>}
        </form>
      )}
    </div>
  );
}
